/**
 * La Central del aliado.
 *
 * Un usuario con rol `aliado` no ve el cotizador de Soham: entra directo aquí.
 * Ve SOLO los encargos de su ramo (la consulta filtra por el claim `ramo` y las
 * reglas de Firestore rechazan cualquier otra), con las fotos y medidas del
 * levantamiento, y colabora con revisiones append-only.
 *
 * Desde 2026-08-18, el aliado del ramo TALLER además tiene su MESA DE TRABAJO
 * (decisión de Juan): edita sus propios costos —placas, minuto de máquina,
 * herrajes, aprovechamiento—, captura los despieces de sus piezas, y corre el
 * levantamiento de fabricación para cotizar lo suyo. La razón es de adopción:
 * una herramienta que solo muestra encargos se queda arrumbada; una que arroja
 * lo que de verdad debe cobrar por su trabajo, se vuelve suya. La transparencia
 * es deliberada: sus costos son la base, y el precio final al cliente lo arma
 * Soham sumando instalación y comisión con el modelo que elijan juntos.
 */
import { useEffect, useState } from 'react'
import * as store from '../datos/store.js'
import { usuario, salir, claimsActuales } from '../firebase.js'
import { RAMOS, NOMBRE_ESTADO } from '../motor/encargos.js'
import { TALLER_BASE } from '../motor/taller.js'
import { BarraSimple, SelloEstado, Piezas, Revisiones, FormRevision } from './Encargo.jsx'
import { PanelTaller, PanelFabricacion } from './Configuracion.jsx'
import Taller from './Taller.jsx'

const fechaCorta = iso => (iso || '').slice(0, 10)

export default function Aliado() {
  const [ramo, setRamo] = useState(undefined)
  const [encargos, setEncargos] = useState(null)
  const [abierto, setAbierto] = useState(null)
  const [seccion, setSeccion] = useState('encargos')

  /* La mesa del taller lee catálogo, despieces y su configuración — todo con
     sesión basta para LEER; escribir taller/despieces lo permiten las reglas
     solo a este ramo. */
  const [productos, setProductos] = useState([])
  const [despiecesLista, setDespiecesLista] = useState([])
  const [config, setConfig] = useState([])

  useEffect(() => { claimsActuales().then(c => setRamo(c.ramo || '')) }, [])
  useEffect(() => {
    if (!ramo) return undefined
    return store.suscribirConsulta('encargos', 'ramo', ramo, lista =>
      setEncargos([...lista].sort((a, b) => (b.actualizadoEn || '').localeCompare(a.actualizadoEn || ''))))
  }, [ramo])
  useEffect(() => {
    if (ramo !== 'taller') return undefined
    const subs = [
      store.suscribir('catalogoProductos', setProductos),
      store.suscribir('despieces', setDespiecesLista),
      store.suscribir('config', setConfig)
    ]
    return () => subs.forEach(u => u())
  }, [ramo])

  if (ramo === undefined) return <div className="app"><div className="empty">Cargando…</div></div>
  if (ramo === '') {
    return (
      <div className="app"><main>
        <div className="empty">
          <b>Tu cuenta no tiene un ramo asignado.</b>
          <p>Pide al administrador que complete tu acceso.</p>
          <button className="ib" onClick={() => salir()}>Salir</button>
        </div>
      </main></div>
    )
  }

  const tallerCfg = config.find(c => c.id === 'taller') || {}
  const cfgTaller = { ...TALLER_BASE, ...tallerCfg }
  const mapaDespieces = Object.fromEntries(despiecesLista.map(d => [d.id, d]))
  const encargo = encargos?.find(e => e.id === abierto)

  /* ---------- Cotizar una pieza: el levantamiento de fabricación, suelto ---------- */
  if (seccion === 'cotizar') {
    return <Taller atras={() => setSeccion('encargos')} taller={cfgTaller} />
  }

  /* ---------- Detalle de un encargo ---------- */
  if (encargo) {
    const marcar = async estado => {
      await store.guardar('encargos', { ...encargo, estado, actualizadoEn: new Date().toISOString() })
    }
    return (
      <div className="app">
        <BarraSimple titulo={encargo.nombreProyecto} migaja={RAMOS[ramo]?.nombre || ramo}
          atras={() => setAbierto(null)} acciones={<SelloEstado estado={encargo.estado} />} />
        <main>
          <div className="sec">Piezas encargadas</div>
          <Piezas piezas={encargo.piezas} />
          {encargo.notas && (
            <div className="card"><div className="grow">
              <div className="t">Notas del levantamiento</div>
              <div style={{ marginTop: 4, whiteSpace: 'pre-wrap' }}>{encargo.notas}</div>
            </div></div>
          )}

          <div className="sec">Estado</div>
          <div className="card">
            <div className="grow">
              <div className="t">{NOMBRE_ESTADO[encargo.estado] || encargo.estado}</div>
              <div className="s">Marca tu avance; Soham cierra los estados finales.</div>
            </div>
            {encargo.estado !== 'desarrollo' && (
              <button className="ib" onClick={() => marcar('desarrollo')}>Lo estoy trabajando</button>
            )}
            {encargo.estado !== 'propuesta' && (
              <button className="ib" onClick={() => marcar('propuesta')}>Propuesta lista</button>
            )}
          </div>

          <div className="sec">Revisiones</div>
          <Revisiones encargoId={encargo.id} ramo={ramo} />
          <FormRevision encargoId={encargo.id} ramo={ramo} tipo="desarrollo"
            rotulo="Registrar un avance o cambio" />
        </main>
      </div>
    )
  }

  /* ---------- La Central: encargos + la mesa del taller ---------- */
  const esTaller = ramo === 'taller'
  return (
    <div className="app">
      {/* Sin «Salir» en la barra (corrección de Juan, 2026-08-20): la esquina de arriba
          es del trabajo; la cuenta y el cierre de sesión van al pie, como en el inicio. */}
      <BarraSimple titulo={RAMOS[ramo]?.nombre || 'Encargos'} migaja="Soham Central" />
      <main>

        {esTaller && (
          <div className="tabs" style={{ flexWrap: 'wrap' }}>
            {[['encargos', 'Encargos'], ['costos', 'Mis costos'], ['despieces', 'Mis despieces'], ['cotizar', 'Cotizar una pieza']].map(([k, n]) => (
              <button key={k} className={`tb ${seccion === k ? 'on' : ''}`}
                style={{ flex: '1 1 40%' }} onClick={() => setSeccion(k)}>{n}</button>
            ))}
          </div>
        )}

        {seccion === 'costos' && esTaller && (
          <>
            <div className="nota">
              Estos números son <b>tuyos</b> y esta pantalla es tuya: el precio de TU placa,
              TU minuto de máquina, TU herraje. De aquí sale lo que de verdad cuesta
              fabricar cada pieza. El precio final al cliente lo arma Soham sumando
              instalación y comisión — <b>el modelo se define juntos y a la vista</b>.
            </div>
            <PanelTaller taller={tallerCfg}
              onGuardar={t => store.guardar('config', { ...t, id: 'taller' })} />
          </>
        )}

        {seccion === 'despieces' && esTaller && (
          <PanelFabricacion productos={productos} despieces={mapaDespieces}
            cfgTaller={cfgTaller}
            onGuardarDespiece={(sku, d) => store.guardar('despieces', { ...d, id: sku })} />
        )}

        {seccion === 'encargos' && (
          <>
            <div className="sec">Encargos</div>
            {encargos === null && <div className="empty">Cargando encargos…</div>}
            {encargos?.length === 0 && (
              <div className="empty">
                <b>Todavía no hay encargos.</b>
                <p>Cuando Soham levante un proyecto con piezas de tu ramo, aparecerá aquí en el momento.</p>
              </div>
            )}
            {encargos?.map(e => (
              <div className="card tap" key={e.id} onClick={() => setAbierto(e.id)}>
                <div className="grow">
                  <div className="t">{e.nombreProyecto}</div>
                  <div className="s">{e.piezas?.length || 0} pieza{(e.piezas?.length || 0) === 1 ? '' : 's'} · {fechaCorta(e.actualizadoEn)}</div>
                </div>
                <SelloEstado estado={e.estado} />
              </div>
            ))}
          </>
        )}

        <div className="cuenta">
          <span className="s">Sesión de aliado · {usuario()?.email}</span>
          <button type="button" className="discreto" onClick={() => salir()}>Cerrar sesión</button>
        </div>
      </main>
    </div>
  )
}
